import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Wifi,
  Smartphone,
  Tv,
  Shield,
  Dumbbell,
  Home,
  Building2,
  AlertTriangle,
  Calendar,
  TrendingDown,
  ExternalLink,
  Clock,
  CheckCircle,
  Sparkles,
  Pencil,
  Trash2,
  Plus,
  Save,
  X,
  ChevronRight,
  Phone,
  Mail,
  Globe,
  FileText
} from 'lucide-react';

const contractTypeConfig = {
  energy: {
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-l-green-500'
  },
  internet: {
    icon: Wifi,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-l-blue-500'
  },
  internet_tv: {
    icon: Tv,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-l-indigo-500'
  },
  television: {
    icon: Tv,
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-100',
    borderColor: 'border-l-cyan-500'
  },
  mobile: {
    icon: Smartphone,
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    borderColor: 'border-l-sky-500'
  },
  insurance: {
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-l-purple-500'
  },
  membership: {
    icon: Dumbbell,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-l-amber-500'
  },
  mortgage: {
    icon: Home,
    color: 'text-slate-700',
    bgColor: 'bg-slate-100',
    borderColor: 'border-l-slate-500'
  },
  rent: {
    icon: Building2,
    color: 'text-stone-700',
    bgColor: 'bg-stone-100',
    borderColor: 'border-l-stone-500'
  },
  custom_fixed_cost: {
    icon: AlertTriangle,
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-l-amber-500'
  }
};

const statusConfig = {
  active: { label: 'Actief', color: 'bg-green-100 text-green-700' },
  expiring_soon: { label: 'Verloopt binnenkort', color: 'bg-orange-100 text-orange-700' },
  expired: { label: 'Verlopen', color: 'bg-red-100 text-red-700' }
};

const contractTypes = [
  { value: 'energy', label: 'Energie' },
  { value: 'internet', label: 'Internet' },
  { value: 'internet_tv', label: 'Internet + TV' },
  { value: 'television', label: 'Televisie' },
  { value: 'mobile', label: 'Mobiel abonnement' },
  { value: 'insurance', label: 'Verzekering' },
  { value: 'membership', label: 'Contributie' },
  { value: 'mortgage', label: 'Hypotheek' },
  { value: 'rent', label: 'Huur' },
];

const contractTypeLabels = {
  all: 'Alle',
  energy: 'Energie',
  internet: 'Internet',
  internet_tv: 'Internet + TV',
  television: 'Televisie',
  mobile: 'Mobiel abonnement',
  insurance: 'Verzekering',
  membership: 'Contributie',
  mortgage: 'Hypotheek',
  rent: 'Huur',
};

const contractFilterOptions = [
  { value: 'all', label: 'Alle vaste lasten' },
  { value: 'energy', label: 'Energie' },
  { value: 'internet', label: 'Internet' },
  { value: 'internet_tv', label: 'Internet + TV' },
  { value: 'television', label: 'Televisie' },
  { value: 'mobile', label: 'Mobiel abonnement' },
  { value: 'insurance', label: 'Verzekering' },
  { value: 'membership', label: 'Contributie' },
  { value: 'mortgage', label: 'Hypotheek' },
  { value: 'rent', label: 'Huur' },
];

const contractDocumentTypeOptions = [
  { value: 'contract', label: 'Contract' },
  { value: 'policy', label: 'Polisblad' },
  { value: 'terms', label: 'Voorwaarden' },
  { value: 'year_statement', label: 'Jaaroverzicht' },
  { value: 'invoice', label: 'Factuur' },
  { value: 'other', label: 'Overig document' },
];

const contractDocumentTypeLabels = contractDocumentTypeOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const getReferenceFieldMeta = (type) => {
  switch (type) {
    case 'mobile':
      return {
        primaryLabel: 'Abonnementsnummer',
        primaryPlaceholder: 'Bijv. abonnementsnummer',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klantnummer',
      };
    case 'internet':
    case 'internet_tv':
    case 'television':
      return {
        primaryLabel: 'Abonnementsnummer',
        primaryPlaceholder: 'Bijv. internet- of tv-abonnement',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klantnummer',
      };
    case 'insurance':
      return {
        primaryLabel: 'Relatienummer',
        primaryPlaceholder: 'Bijv. relatienummer bij verzekeraar',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klant- of verzekerdennummer',
      };
    case 'mortgage':
      return {
        primaryLabel: 'Hypotheeknummer',
        primaryPlaceholder: 'Bijv. hypotheeknummer',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klantnummer',
      };
    case 'rent':
      return {
        primaryLabel: 'Huurovereenkomst',
        primaryPlaceholder: 'Bijv. contract- of dossiernummer',
        secondaryLabel: 'Huurdersnummer',
        secondaryPlaceholder: 'Optioneel huurdersnummer',
      };
    case 'membership':
      return {
        primaryLabel: 'Lidmaatschapsnummer',
        primaryPlaceholder: 'Bijv. lidnummer',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klantnummer',
      };
    default:
      return {
        primaryLabel: 'Contractnummer',
        primaryPlaceholder: 'Bijv. contractnummer',
        secondaryLabel: 'Klantnummer',
        secondaryPlaceholder: 'Optioneel klantnummer',
      };
  }
};

const buildReferenceRows = (contract) => {
  const meta = getReferenceFieldMeta(contract.type);
  const rows = [];

  if (contract.contract_reference) {
    rows.push({ label: meta.primaryLabel, value: contract.contract_reference });
  }
  if (contract.customer_number) {
    rows.push({ label: meta.secondaryLabel, value: contract.customer_number });
  }
  if (contract.insurance_policy_number) {
    rows.push({ label: 'Polisnummer', value: contract.insurance_policy_number });
  }
  if (contract.mobile_number) {
    rows.push({ label: 'Mobiel nummer', value: contract.mobile_number });
  }

  return rows;
};

const ContractCard = ({
  contract,
  onEdit,
  onDelete,
  attachments,
  onUpload,
  onDownload,
  onOpenAttachment,
  onDeleteAttachment,
  uploading,
  onCompare,
  onOpenDossier,
}) => {
  const config = contractTypeConfig[contract.type] || contractTypeConfig.membership;
  const status = statusConfig[contract.status] || statusConfig.active;
  const Icon = config.icon;
  const daysProgress = Math.max(0, Math.min(100, (Number(contract.days_until_end || 0) / 365) * 100));
  const isIndefiniteRent = contract.type === 'rent' && contract.rent_is_indefinite;
  const referenceRows = buildReferenceRows(contract);
  const contactReference = referenceRows[0]?.value;
  const primaryAttachment = attachments[0];
  const nearestMortgageFixedUntil = contract.type === 'mortgage'
    ? (contract.mortgage_loan_parts || [])
        .filter((part) => part.fixed_until_date)
        .sort((a, b) => new Date(a.fixed_until_date) - new Date(b.fixed_until_date))[0]
    : null;

  return (
    <Card className={`shadow-card hover:shadow-card-hover transition-all border-l-4 ${config.borderColor}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${config.color}`} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">{contract.provider}</h3>
              <p className="text-sm text-muted-foreground">
                {contract.type === 'custom_fixed_cost'
                  ? (contract.custom_type_label || 'Eigen vaste last')
                  : (contractTypeLabels[contract.type] || contract.type)}
              </p>
            </div>
          </div>
          <Badge className={status.color}>{isIndefiniteRent ? 'Onbepaalde tijd' : status.label}</Badge>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-muted-foreground">Loopt tot</p>
            <p className="font-medium">
              {isIndefiniteRent ? 'Onbepaalde tijd' : (contract.end_date ? new Date(contract.end_date).toLocaleDateString('nl-NL') : '-')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Referentie</p>
            <p className="font-medium text-foreground/80">
              {contactReference || 'Nog niet ingevuld'}
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {(contract.start_date || contactReference || contract.mobile_number || contract.provider_phone || contract.provider_email) && (
            <div className="rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-1">
              {contract.start_date && (
                <p>Ingegaan op: {new Date(contract.start_date).toLocaleDateString('nl-NL')}</p>
              )}
              {contactReference && (
                <p>{referenceRows[0].label}: {contactReference}</p>
              )}
              {contract.mobile_number && (
                <p>Mobiel nummer: {contract.mobile_number}</p>
              )}
              {contract.provider_phone && (
                <p>Telefoon aanbieder: {contract.provider_phone}</p>
              )}
              {contract.provider_email && (
                <p>E-mail aanbieder: {contract.provider_email}</p>
              )}
            </div>
          )}
          {isIndefiniteRent ? (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              Deze huur loopt voor onbepaalde tijd.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  Nog {Number(contract.days_until_end || 0)} dagen
                </span>
                <span className="text-muted-foreground">
                  {contract.end_date ? new Date(contract.end_date).toLocaleDateString('nl-NL') : '-'}
                </span>
              </div>
              <Progress value={100 - daysProgress} className="h-2" />
            </>
          )}
          {nearestMortgageFixedUntil && (
            <p className="text-xs text-muted-foreground">
              Rente vast tot {new Date(nearestMortgageFixedUntil.fixed_until_date).toLocaleDateString('nl-NL')}
              {nearestMortgageFixedUntil.name ? ` · ${nearestMortgageFixedUntil.name}` : ''}
            </p>
          )}
          {contract.type === 'insurance' && (
            <div className="text-xs text-muted-foreground">
              <p>
                Eigen risico: €{Number(contract.insurance_deductible_used || 0).toFixed(0)} / €
                {Number(contract.insurance_deductible_total || 0).toFixed(0)}
              </p>
              {(contract.insurance_package_name || contract.insurance_coverage_level) && (
                <p>
                  {contract.insurance_package_name ? contract.insurance_package_name : 'Pakket'}{contract.insurance_coverage_level ? ` · ${contract.insurance_coverage_level}` : ''}
                </p>
              )}
            </div>
          )}
        </div>

        {contract.potential_savings > 0 && (
          <div className="p-3 rounded-lg bg-green-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Potentiële besparing
              </span>
            </div>
            <span className="font-mono font-bold text-green-600">
              €{contract.potential_savings}/jaar
            </span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button className="rounded-full" variant="outline" onClick={() => onCompare(contract)}>
            Vergelijk
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <Button
            className="rounded-full"
            variant="outline"
            onClick={() => onOpenDossier(contract)}
          >
            <FileText className="w-4 h-4 mr-2" />
            {primaryAttachment ? 'Contract inzien' : 'Open dossier'}
          </Button>
        </div>
        {(contract.provider_phone || contract.provider_email || contract.provider_website) && (
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button
              className="rounded-full"
              variant="secondary"
              onClick={() => contract.provider_phone && window.open(`tel:${contract.provider_phone}`, '_self')}
              disabled={!contract.provider_phone}
            >
              <Phone className="w-4 h-4 mr-2" />
              Bel
            </Button>
            <Button
              className="rounded-full"
              variant="secondary"
              onClick={() => contract.provider_email && window.open(`mailto:${contract.provider_email}`, '_self')}
              disabled={!contract.provider_email}
            >
              <Mail className="w-4 h-4 mr-2" />
              Mail
            </Button>
            <Button
              className="rounded-full"
              variant="secondary"
              onClick={() =>
                contract.provider_website &&
                window.open(
                  contract.provider_website.startsWith('http') ? contract.provider_website : `https://${contract.provider_website}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              disabled={!contract.provider_website}
            >
              <Globe className="w-4 h-4 mr-2" />
              Website
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button className="rounded-full" variant="secondary" onClick={() => onEdit(contract)}>
            <Pencil className="w-4 h-4 mr-2" />
            Wijzig
          </Button>
          <Button className="rounded-full" variant="destructive" onClick={() => onDelete(contract.id)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Verwijder
          </Button>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Bijlagen</p>
            <label className="text-xs font-medium text-primary cursor-pointer">
              {uploading ? 'Uploaden...' : 'Upload'}
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(contract.id, file);
                  e.target.value = '';
                }}
                disabled={uploading}
              />
            </label>
          </div>
          {attachments.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nog geen bijlagen.</p>
          ) : (
            <div className="space-y-1">
              {attachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between text-xs">
                  <span className="truncate max-w-[200px]">{att.filename}</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => onDownload(att)}>
                      Download
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDeleteAttachment(att.id)}>
                      Verwijder
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Contracts() {
  const navigate = useNavigate();
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [contracts, setContracts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingContractId, setUploadingContractId] = useState(null);
  const [isMobileFormOpen, setIsMobileFormOpen] = useState(false);
  const [isDesktopFormOpen, setIsDesktopFormOpen] = useState(false);
  const [dossierContract, setDossierContract] = useState(null);
  const [dossierDocumentType, setDossierDocumentType] = useState('contract');
  const [form, setForm] = useState({
    type: 'energy',
    provider: '',
    start_date: '',
    end_date: '',
    contract_reference: '',
    customer_number: '',
    mobile_number: '',
    provider_phone: '',
    provider_email: '',
    provider_website: '',
    rent_is_indefinite: false,
    insurance_package_name: '',
    insurance_coverage_level: '',
    insurance_policy_number: '',
    insurance_contract_year: '',
    insurance_deductible_total: '',
    insurance_deductible_used: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [contractsRes, alertsRes, attachmentsRes] = await Promise.all([
        apiClient.get('/contracts').catch(() => ({ data: [] })),
        apiClient.get('/contracts/alerts').catch(() => ({ data: [] })),
        apiClient.get('/contracts/attachments').catch(() => ({ data: [] })),
      ]);
      setContracts(contractsRes.data);
      setAlerts(alertsRes.data);
      setAttachments(Array.isArray(attachmentsRes.data) ? attachmentsRes.data : []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onResize = () => setIsCompactViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setIsMobileFormOpen(false);
    setIsDesktopFormOpen(false);
    setForm({
      type: 'energy',
      provider: '',
      custom_type_label: '',
      start_date: '',
      end_date: '',
      contract_reference: '',
      customer_number: '',
      mobile_number: '',
      provider_phone: '',
      provider_email: '',
      provider_website: '',
      rent_is_indefinite: false,
      insurance_package_name: '',
      insurance_coverage_level: '',
      insurance_policy_number: '',
      insurance_contract_year: '',
      insurance_deductible_total: '',
      insurance_deductible_used: '',
    });
  };

  const fetchAttachments = useCallback(async () => {
    try {
      const res = await apiClient.get('/contracts/attachments');
      setAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching contract attachments:', error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dossierPayload = {
      ...form,
      custom_type_label: form.custom_type_label?.trim() || null,
      provider: form.provider?.trim(),
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      contract_reference: form.contract_reference?.trim() || null,
      customer_number: form.customer_number?.trim() || null,
      mobile_number: form.mobile_number?.trim() || null,
      provider_phone: form.provider_phone?.trim() || null,
      provider_email: form.provider_email?.trim() || null,
      provider_website: form.provider_website?.trim() || null,
      rent_is_indefinite: Boolean(form.rent_is_indefinite),
      insurance_contract_year: form.insurance_contract_year ? Number(form.insurance_contract_year) : null,
      insurance_deductible_total: form.insurance_deductible_total ? Number(form.insurance_deductible_total) : null,
      insurance_deductible_used: form.insurance_deductible_used ? Number(form.insurance_deductible_used) : null,
    };
    const payload = editingId
      ? dossierPayload
      : {
          ...dossierPayload,
          monthly_cost: 0,
          contract_type: 'fixed',
          market_average: 0,
        };

    try {
      if (editingId) {
        await apiClient.put(`/contracts/${editingId}`, payload);
      } else {
        await apiClient.post('/contracts', payload);
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving contract:', error);
    }
  };

  const startEdit = (contract) => {
    setEditingId(contract.id);
    setIsMobileFormOpen(true);
    setIsDesktopFormOpen(true);
    setForm({
      type: contract.type,
      provider: contract.provider,
      custom_type_label: contract.custom_type_label || '',
      start_date: contract.start_date ? new Date(contract.start_date).toISOString().split('T')[0] : '',
      end_date: contract.end_date ? new Date(contract.end_date).toISOString().split('T')[0] : '',
      contract_reference: contract.contract_reference || '',
      customer_number: contract.customer_number || '',
      mobile_number: contract.mobile_number || '',
      provider_phone: contract.provider_phone || '',
      provider_email: contract.provider_email || '',
      provider_website: contract.provider_website || '',
      rent_is_indefinite: Boolean(contract.rent_is_indefinite),
      insurance_package_name: contract.insurance_package_name || '',
      insurance_coverage_level: contract.insurance_coverage_level || '',
      insurance_policy_number: contract.insurance_policy_number || '',
      insurance_contract_year: contract.insurance_contract_year != null ? String(contract.insurance_contract_year) : '',
      insurance_deductible_total: contract.insurance_deductible_total != null ? String(contract.insurance_deductible_total) : '',
      insurance_deductible_used: contract.insurance_deductible_used != null ? String(contract.insurance_deductible_used) : '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteContract = async (id) => {
    if (!window.confirm('Contract verwijderen?')) return;
    try {
      await apiClient.delete(`/contracts/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting contract:', error);
    }
  };

  const uploadAttachment = async (contractId, file, documentType = 'contract') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    setUploadingContractId(contractId);
    try {
      await apiClient.post(`/contracts/${contractId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchAttachments();
    } catch (error) {
      console.error('Error uploading attachment:', error);
    } finally {
      setUploadingContractId(null);
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const res = await apiClient.get(`/contracts/attachments/${attachment.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const openAttachment = async (attachment) => {
    try {
      const res = await apiClient.get(`/contracts/attachments/${attachment.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      console.error('Error opening attachment:', error);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('Bijlage verwijderen?')) return;
    try {
      await apiClient.delete(`/contracts/attachments/${attachmentId}`);
      await fetchAttachments();
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const openComparison = (contract) => {
    const type = contract?.type;
    const baseUrls = {
      energy: 'https://www.independer.nl/energie/intro.aspx',
      internet: 'https://www.independer.nl/telecom/internet-tv/intro.aspx',
      internet_tv: 'https://www.independer.nl/telecom/internet-tv/intro.aspx',
      television: 'https://www.independer.nl/telecom/internet-tv/intro.aspx',
      mobile: 'https://www.independer.nl/telecom/mobiel/intro.aspx',
      insurance: 'https://www.independer.nl/zorgverzekering/intro.aspx',
      mortgage: 'https://www.independer.nl/hypotheek/',
    };
    const mapped = baseUrls[type];
    if (mapped) {
      window.open(mapped, '_blank', 'noopener,noreferrer');
      return;
    }
    const provider = String(contract?.provider || '').trim();
    const query = provider
      ? `${provider} ${type || 'contract'} vergelijken`
      : `${type || 'contract'} vergelijken`;
    const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const visibleContracts = useMemo(
    () => contracts.filter((c) => c.type !== 'custom_fixed_cost'),
    [contracts],
  );
  const filteredContracts = activeTab === 'all'
    ? visibleContracts
    : visibleContracts.filter(c => c.type === activeTab);
  const hasContracts = visibleContracts.length > 0;
  const showContractsGettingStarted = !hasContracts;

  const insuranceContracts = useMemo(
    () => visibleContracts.filter((c) => c.type === 'insurance'),
    [visibleContracts],
  );
  const insuranceYearlyPremium = useMemo(() => {
    return insuranceContracts.reduce((sum, c) => sum + Number(c.monthly_cost || 0), 0) * 12;
  }, [insuranceContracts]);
  const insuranceDeductibleRemaining = useMemo(() => {
    return insuranceContracts.reduce((sum, c) => {
      const total = Number(c.insurance_deductible_total || 0);
      const used = Number(c.insurance_deductible_used || 0);
      return sum + Math.max(0, total - used);
    }, 0);
  }, [insuranceContracts]);
  const now = useMemo(() => new Date(), []);
  const isNovember = useMemo(() => now.getMonth() === 10, [now]);
  const daysUntilYearEnd = useMemo(() => {
    const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    return Math.ceil((yearEnd - now) / (1000 * 60 * 60 * 24));
  }, [now]);
  const showInsuranceSwitchAlert = useMemo(() => {
    if (isNovember) return true;
    if (insuranceContracts.length === 0) return false;
    const within30DaysOfEnd = insuranceContracts.some((c) => {
      if (!c.end_date) return false;
      const daysLeft = Math.ceil((new Date(c.end_date) - now) / (1000 * 60 * 60 * 24));
      return daysLeft >= 0 && daysLeft <= 30;
    });
    return within30DaysOfEnd;
  }, [insuranceContracts, isNovember, now]);

  const attachmentsByContract = useMemo(() => {
    return attachments.reduce((acc, att) => {
      if (!acc[att.contract_id]) acc[att.contract_id] = [];
      acc[att.contract_id].push(att);
      return acc;
    }, {});
  }, [attachments]);
  const dossierAttachments = dossierContract ? (attachmentsByContract[dossierContract.id] || []) : [];

  const totalPotentialSavings = visibleContracts.reduce((sum, c) => sum + c.potential_savings, 0);
  const upcomingPayments = [...visibleContracts]
    .filter((c) => c.next_payment_date)
    .sort((a, b) => new Date(a.next_payment_date) - new Date(b.next_payment_date));
  const upcoming7Days = upcomingPayments.filter((c) => {
    const days = Math.ceil((new Date(c.next_payment_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 7;
  });
  const scrollToContractForm = () => {
    setIsMobileFormOpen(true);
    setIsDesktopFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const openDossier = (contract) => setDossierContract(contract);
  const editFromDossier = (contract) => {
    setDossierContract(null);
    setDossierDocumentType('contract');
    startEdit(contract);
  };
  const isRentForm = form.type === 'rent';
  const isMobileContractForm = form.type === 'mobile';
  const referenceFieldMeta = getReferenceFieldMeta(form.type);
  const getAttachmentTypeLabel = (documentType) =>
    contractDocumentTypeLabels[documentType] || 'Document';

  const dossierContent = dossierContract ? (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={() => editFromDossier(dossierContract)}>
          <Pencil className="mr-2 h-4 w-4" />
          Bewerk contract
        </Button>
        <Button
          className="rounded-full"
          variant="outline"
          onClick={() => {
            setDossierContract(null);
            navigate('/fixed-costs');
          }}
        >
          Naar Vaste lasten
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border bg-muted/20 p-4">
          <p className="text-xs font-medium text-muted-foreground">Type contract</p>
          <p className="mt-1 font-medium">
            {contractTypeLabels[dossierContract.type] || dossierContract.type}
          </p>
          {dossierContract.start_date && (
            <p className="mt-2 text-sm text-muted-foreground">
              Ingegaan op {new Date(dossierContract.start_date).toLocaleDateString('nl-NL')}
            </p>
          )}
          {dossierContract.end_date && (
            <p className="mt-1 text-sm text-muted-foreground">
              Einddatum {new Date(dossierContract.end_date).toLocaleDateString('nl-NL')}
            </p>
          )}
        </div>
        <div className="rounded-xl border bg-muted/20 p-4">
          <p className="text-xs font-medium text-muted-foreground">Referenties</p>
          <div className="mt-2 space-y-1 text-sm">
            {buildReferenceRows(dossierContract).map((row) => (
              <p key={`${row.label}-${row.value}`}>{row.label}: {row.value}</p>
            ))}
            {!dossierContract.contract_reference && !dossierContract.customer_number && !dossierContract.insurance_policy_number && !dossierContract.mobile_number && (
              <p className="text-muted-foreground">Nog geen referenties ingevuld.</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border p-4">
        <p className="text-xs font-medium text-muted-foreground">Contactgegevens aanbieder</p>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          <Button
            className="rounded-full justify-center"
            variant="secondary"
            onClick={() => dossierContract.provider_phone && window.open(`tel:${dossierContract.provider_phone}`, '_self')}
            disabled={!dossierContract.provider_phone}
          >
            <Phone className="w-4 h-4 mr-2" />
            Bel aanbieder
          </Button>
          <Button
            className="rounded-full justify-center"
            variant="secondary"
            onClick={() => dossierContract.provider_email && window.open(`mailto:${dossierContract.provider_email}`, '_self')}
            disabled={!dossierContract.provider_email}
          >
            <Mail className="w-4 h-4 mr-2" />
            Mail aanbieder
          </Button>
          <Button
            className="rounded-full justify-center"
            variant="secondary"
            onClick={() =>
              dossierContract.provider_website &&
              window.open(
                dossierContract.provider_website.startsWith('http') ? dossierContract.provider_website : `https://${dossierContract.provider_website}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            disabled={!dossierContract.provider_website}
          >
            <Globe className="w-4 h-4 mr-2" />
            Website
          </Button>
        </div>
      </div>

      <div className="rounded-xl border p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">Documenten</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Voeg contracten, polisbladen, voorwaarden of jaaroverzichten toe en open ze vanaf hier.
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-[240px] md:min-w-[240px]">
            <Select value={dossierDocumentType} onValueChange={setDossierDocumentType}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Kies documentsoort" />
              </SelectTrigger>
              <SelectContent>
                {contractDocumentTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-medium text-primary cursor-pointer">
              Upload document
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadAttachment(dossierContract.id, file, dossierDocumentType);
                  e.target.value = '';
                }}
                disabled={uploadingContractId === dossierContract.id}
              />
            </label>
          </div>
        </div>

        {dossierAttachments.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed p-5 text-sm text-muted-foreground">
            Nog geen documenten toegevoegd. Upload hier bijvoorbeeld je contract, polis, voorwaarden of jaaroverzicht.
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {dossierAttachments.map((att) => (
              <div key={att.id} className="flex flex-col gap-3 rounded-xl border p-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{att.filename}</p>
                    <Badge variant="secondary" className="rounded-full">
                      {getAttachmentTypeLabel(att.document_type)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Geüpload op {new Date(att.uploaded_at).toLocaleDateString('nl-NL')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openAttachment(att)}>
                    Open
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadAttachment(att)}>
                    Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteAttachment(att.id)}>
                    Verwijder
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  const contractForm = (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Type contract</p>
        <Select value={form.type} onValueChange={(value) => setForm((p) => ({ ...p, type: value }))}>
          <SelectTrigger>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {contractTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Aanbieder of provider</p>
        <Input
          placeholder="Bijv. Odido, Ziggo of Nationale-Nederlanden"
          value={form.provider}
          onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))}
          required
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Ingangsdatum</p>
        <Input
          type="date"
          value={form.start_date}
          onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{isRentForm ? 'Einddatum huurcontract' : 'Einddatum'}</p>
        <Input
          type="date"
          value={form.end_date}
          onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
          required={!isRentForm || !form.rent_is_indefinite}
          disabled={isRentForm && form.rent_is_indefinite}
        />
        {isRentForm && (
          <label className="flex items-center gap-2 text-sm pt-1">
            <input
              type="checkbox"
              checked={Boolean(form.rent_is_indefinite)}
              onChange={(e) => setForm((p) => ({
                ...p,
                rent_is_indefinite: e.target.checked,
                end_date: e.target.checked ? '' : p.end_date,
              }))}
            />
            Huur is voor onbepaalde tijd
          </label>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{referenceFieldMeta.primaryLabel}</p>
        <Input
          placeholder={referenceFieldMeta.primaryPlaceholder}
          value={form.contract_reference}
          onChange={(e) => setForm((p) => ({ ...p, contract_reference: e.target.value }))}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{referenceFieldMeta.secondaryLabel}</p>
        <Input
          placeholder={referenceFieldMeta.secondaryPlaceholder}
          value={form.customer_number}
          onChange={(e) => setForm((p) => ({ ...p, customer_number: e.target.value }))}
        />
      </div>
      {isMobileContractForm && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Mobiel nummer</p>
          <Input
            placeholder="Bijv. 06 12345678"
            value={form.mobile_number}
            onChange={(e) => setForm((p) => ({ ...p, mobile_number: e.target.value }))}
          />
        </div>
      )}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Telefoon aanbieder</p>
        <Input
          placeholder="Bijv. 088 123 45 67"
          value={form.provider_phone}
          onChange={(e) => setForm((p) => ({ ...p, provider_phone: e.target.value }))}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">E-mail aanbieder</p>
        <Input
          type="email"
          placeholder="Bijv. service@aanbieder.nl"
          value={form.provider_email}
          onChange={(e) => setForm((p) => ({ ...p, provider_email: e.target.value }))}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Website aanbieder</p>
        <Input
          placeholder="Bijv. www.odido.nl"
          value={form.provider_website}
          onChange={(e) => setForm((p) => ({ ...p, provider_website: e.target.value }))}
        />
      </div>
      {form.type === 'insurance' && (
        <>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Polis of pakket</p>
            <Input
              placeholder="Bijv. Basis + aanvullend"
              value={form.insurance_package_name}
              onChange={(e) => setForm((p) => ({ ...p, insurance_package_name: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Dekking</p>
            <Input
              placeholder="Bijv. allrisk of aanvullend"
              value={form.insurance_coverage_level}
              onChange={(e) => setForm((p) => ({ ...p, insurance_coverage_level: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Polisnummer</p>
            <Input
              placeholder="Polisnummer"
              value={form.insurance_policy_number}
              onChange={(e) => setForm((p) => ({ ...p, insurance_policy_number: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Contractjaar</p>
            <Input
              type="number"
              min="2020"
              step="1"
              placeholder="Bijv. 2026"
              value={form.insurance_contract_year}
              onChange={(e) => setForm((p) => ({ ...p, insurance_contract_year: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Eigen risico totaal</p>
            <Input
              type="number"
              step="1"
              min="0"
              placeholder="0"
              value={form.insurance_deductible_total}
              onChange={(e) => setForm((p) => ({ ...p, insurance_deductible_total: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Eigen risico verbruikt</p>
            <Input
              type="number"
              step="1"
              min="0"
              placeholder="0"
              value={form.insurance_deductible_used}
              onChange={(e) => setForm((p) => ({ ...p, insurance_deductible_used: e.target.value }))}
            />
          </div>
        </>
      )}
      <div className="md:col-span-3 rounded-2xl border bg-muted/30 p-4 text-sm text-muted-foreground">
        Maandbedrag, betaalrekening en betaaldagen beheer je in <span className="font-medium text-foreground">Vaste lasten</span>.
        Zo blijft dit contractscherm gericht op dossier, looptijd en contactgegevens.
      </div>
      <div className="md:col-span-3 flex flex-wrap gap-2">
        <Button type="submit" className="rounded-full">
          {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {editingId ? 'Opslaan' : 'Toevoegen'}
        </Button>
        {editingId && (
          <Button type="button" variant="outline" className="rounded-full" onClick={resetForm}>
            <X className="w-4 h-4 mr-2" />
            Annuleren
          </Button>
        )}
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <AppPageIntro
        eyebrow={isCompactViewport ? null : "Dossier en signalering"}
        title="Contract Bewaking"
        description="Beheer hier je contractgegevens, documenten en overstapmomenten."
        badges={isCompactViewport ? [] : ['Contractdossier', 'Overstapmomenten', 'Referenties']}
        compact={isCompactViewport}
      />

      <Card className="hidden shadow-card border-slate-200 bg-slate-50/70 md:block">
        <CardContent className="grid gap-4 p-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Dossier</Badge>
              <Badge variant="outline">Signalering</Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold">Waar gebruik je Contracten voor?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Hier bewaar je de contractdetails die je later wilt kunnen terugpakken: ingangsdatum, einddatum, contractnummer,
              klantnummer, mobiel nummer en signalen rond verlengen of overstappen.
            </p>
          </div>
          <div className="rounded-2xl border bg-background p-4">
            <p className="font-medium">Bedrag en betaaldag beheren?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Voor de maandelijkse druk op je budget, de betaalrekening en de verwachte afschrijving gebruik je liever Vaste lasten.
            </p>
            <Button className="mt-4 rounded-full" variant="outline" onClick={() => navigate('/fixed-costs')}>
              Naar Vaste lasten
            </Button>
          </div>
        </CardContent>
      </Card>

      {showContractsGettingStarted && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Begin met je eerste contracten
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">1. Voeg een contract toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Start met energie, internet, verzekering, huur of hypotheek.
                Mobiel en tv-contracten kun je hier ook bewaken.
              </p>
              <Button className="mt-4 w-full rounded-full" onClick={scrollToContractForm}>
                Naar formulier
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">2. Voeg referenties toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Leg contractnummer, klantnummer of polisnummer vast zodat je later sneller terugvindt wat je nodig hebt.
              </p>
              <Button className="mt-4 w-full rounded-full" variant="outline" onClick={scrollToContractForm}>
                Vul referenties in
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">3. Beheer betaalinformatie apart</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Voor maandbedragen, betaaldagen en betaalrekening gebruik je Vaste lasten. Zo blijft dit contractdossier rustiger.
              </p>
              <Button className="mt-4 w-full rounded-full" variant="outline" onClick={() => navigate('/fixed-costs')}>
                Naar Vaste lasten
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="md:hidden">
        <Card className="shadow-card">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-medium">{editingId ? 'Contract wijzigen' : 'Contract toevoegen'}</p>
              <p className="text-sm text-muted-foreground">Open een compact formulier om dossier- en contractgegevens in te vullen.</p>
            </div>
            <Button className="rounded-full" onClick={() => setIsMobileFormOpen(true)}>
              {editingId ? 'Open formulier' : 'Toevoegen'}
            </Button>
          </CardContent>
        </Card>
        <Drawer open={isMobileFormOpen} onOpenChange={setIsMobileFormOpen}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader>
              <DrawerTitle>{editingId ? 'Contract wijzigen' : 'Contract toevoegen'}</DrawerTitle>
              <DrawerDescription>
                Voeg hier rustig contractnummer, looptijd en aanbiedergegevens toe zonder dat het hele scherm volloopt.
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto px-4 pb-6">
              {contractForm}
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      <div className="hidden md:block">
        <Collapsible open={isDesktopFormOpen} onOpenChange={setIsDesktopFormOpen}>
          <Card className="shadow-card">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="font-heading">{editingId ? 'Contract wijzigen' : 'Contract toevoegen'}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Open het formulier alleen wanneer je iets wilt toevoegen of aanpassen. Betaal- en budgetinformatie beheer je apart bij Vaste lasten.
                </p>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant={isDesktopFormOpen ? 'outline' : 'default'} className="rounded-full">
                  {isDesktopFormOpen ? 'Formulier inklappen' : (editingId ? 'Wijzigen' : 'Toevoegen')}
                </Button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                {contractForm}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Actieve contracten</p>
                <p className="font-mono text-2xl font-bold">{contracts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Vereisen aandacht</p>
                <p className="font-mono text-2xl font-bold text-red-600">{alerts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale besparing mogelijk</p>
                <p className="font-mono text-2xl font-bold text-green-600">€{totalPotentialSavings}/jaar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {insuranceContracts.length > 0 && (
          <Card className="shadow-card border-emerald-200 bg-emerald-50/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Zorgverzekering overzicht</p>
                  <p className="font-mono text-xl font-bold text-emerald-700">€{insuranceYearlyPremium.toFixed(0)}/jaar</p>
                  <p className="text-xs text-muted-foreground">
                    Eigen risico restant: €{insuranceDeductibleRemaining.toFixed(0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="shadow-card border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-800">Contracten vereisen aandacht</h3>
                <p className="text-sm text-orange-700 mt-1">
                  {alerts.length} contract(en) of rentevaste momenten vragen binnen 90 dagen om aandacht.
                </p>
                <div className="mt-3 space-y-2">
                  {alerts.slice(0, 3).map((alert) => (
                    <div key={`${alert.contract_id}-${alert.type}-${alert.loan_part_name || 'base'}`} className="rounded-xl border border-orange-200 bg-white/80 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-orange-900">
                            {alert.provider}
                            {alert.loan_part_name ? ` · ${alert.loan_part_name}` : ''}
                          </p>
                          <p className="mt-1 text-sm text-orange-800">{alert.message}</p>
                        </div>
                        <Badge className={alert.alert_level === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                          {alert.days_until_end} dagen
                        </Badge>
                      </div>
                      {alert.action_text && (
                        <p className="mt-2 text-xs font-medium text-orange-700">{alert.action_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {upcoming7Days.length > 0 && (
        <Card className="shadow-card border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Betalingen komende 7 dagen</h3>
                <p className="text-sm text-blue-700 mt-1">
                  {upcoming7Days.length} vaste last(en) worden binnenkort afgeschreven of moeten handmatig betaald worden.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {insuranceContracts.length > 0 && showInsuranceSwitchAlert && (
        <Card className="shadow-card border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div>
                <h3 className="font-semibold text-emerald-800">Overstapmoment zorgverzekering</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  {isNovember
                    ? 'November is hét moment om je zorgverzekering te vergelijken.'
                    : `Er zijn contracten die binnen 30 dagen aflopen (${daysUntilYearEnd} dagen tot jaar-einde).`}
                  {' '}Wil je overstappen of je pakket aanpassen?
                </p>
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open('https://www.independer.nl/zorgverzekering/intro.aspx', '_blank', 'noopener,noreferrer')}
                  >
                    Vergelijk zorgverzekeringen
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <div className="md:hidden">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter vaste lasten" />
            </SelectTrigger>
            <SelectContent>
              {contractFilterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="hidden md:block">
          <div className="-mx-1 overflow-x-auto pb-1">
            <TabsList className="flex h-auto min-w-max items-center gap-2 bg-transparent p-1">
            <TabsTrigger value="all" data-testid="tab-all" className="rounded-full border border-border bg-background px-4 py-2">
              Alle
            </TabsTrigger>
            <TabsTrigger value="energy" data-testid="tab-energy" className="rounded-full border border-border bg-background px-4 py-2">
            <Zap className="w-4 h-4 mr-1" /> Energie
            </TabsTrigger>
            <TabsTrigger value="internet" data-testid="tab-internet" className="rounded-full border border-border bg-background px-4 py-2">
            <Wifi className="w-4 h-4 mr-1" /> Internet
            </TabsTrigger>
            <TabsTrigger value="internet_tv" data-testid="tab-internet-tv" className="rounded-full border border-border bg-background px-4 py-2">
            <Tv className="w-4 h-4 mr-1" /> Internet + TV
            </TabsTrigger>
            <TabsTrigger value="television" data-testid="tab-television" className="rounded-full border border-border bg-background px-4 py-2">
            <Tv className="w-4 h-4 mr-1" /> Televisie
            </TabsTrigger>
            <TabsTrigger value="mobile" data-testid="tab-mobile" className="rounded-full border border-border bg-background px-4 py-2">
            <Smartphone className="w-4 h-4 mr-1" /> Mobiel
            </TabsTrigger>
            <TabsTrigger value="insurance" data-testid="tab-insurance" className="rounded-full border border-border bg-background px-4 py-2">
            <Shield className="w-4 h-4 mr-1" /> Verzekering
            </TabsTrigger>
            <TabsTrigger value="membership" data-testid="tab-membership" className="rounded-full border border-border bg-background px-4 py-2">
            <Dumbbell className="w-4 h-4 mr-1" /> Contributie
            </TabsTrigger>
            <TabsTrigger value="mortgage" data-testid="tab-mortgage" className="rounded-full border border-border bg-background px-4 py-2">
            <Home className="w-4 h-4 mr-1" /> Hypotheek
            </TabsTrigger>
            <TabsTrigger value="rent" data-testid="tab-rent" className="rounded-full border border-border bg-background px-4 py-2">
              <Building2 className="w-4 h-4 mr-1" /> Huur
            </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-6">
          {filteredContracts.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                  <CheckCircle className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-medium">
                  {hasContracts ? 'Geen contracten in dit filter' : 'Nog geen contracten toegevoegd'}
                </p>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  {hasContracts
                    ? 'Kies een ander filter of voeg een nieuw contract toe.'
                    : 'Voeg bovenaan je eerste contract toe om looptijden, betaalmomenten en besparingskansen te volgen.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContracts.map((contract) => (
                <ContractCard
                  key={contract.id}
                  contract={contract}
                  onEdit={startEdit}
                  onDelete={deleteContract}
                  attachments={attachmentsByContract[contract.id] || []}
                  onUpload={uploadAttachment}
                  onDownload={downloadAttachment}
                  onOpenAttachment={openAttachment}
                  onDeleteAttachment={deleteAttachment}
                  uploading={uploadingContractId === contract.id}
                  onCompare={openComparison}
                  onOpenDossier={openDossier}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {isCompactViewport ? (
        <Drawer
          open={Boolean(dossierContract)}
          onOpenChange={(open) => {
            if (!open) {
              setDossierContract(null);
              setDossierDocumentType('contract');
            }
          }}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>{dossierContract ? `Contractdossier · ${dossierContract.provider}` : 'Contractdossier'}</DrawerTitle>
              <DrawerDescription>
                Bekijk hier contractgegevens, contactinformatie en alle gekoppelde documenten op één plek.
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto px-4 pb-6">
              {dossierContent}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={Boolean(dossierContract)}
          onOpenChange={(open) => {
            if (!open) {
              setDossierContract(null);
              setDossierDocumentType('contract');
            }
          }}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {dossierContract ? `Contractdossier · ${dossierContract.provider}` : 'Contractdossier'}
              </DialogTitle>
              <DialogDescription>
                Bekijk hier contractgegevens, contactinformatie en alle gekoppelde documenten op één plek.
              </DialogDescription>
            </DialogHeader>
            {dossierContent}
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}
